import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { ConversationSettingsDetailDto, ConversationTopicDetailDto, ConversationTopicDto } from '../api/types';
import { effortDisplayName } from '../lib/model-names';

const EFFORTS = ['low', 'medium', 'high', 'xhigh', 'max'] as const;

/**
 * AI.4 (18/09/2026) — esforço SÓ desta sessão da Conversa. Pedido do dono: poder subir o esforço para um tema
 * mais difícil sem mexer na preferência global nem em terminais fora do TermDeck — "alterar somente em novas
 * sessões: o usuário limpa a sessão e, na sessão nova, escolhe o esforço daquela sessão".
 * Com a sessão nativa viva o controle vira leitura (o esforço de uma sessão em curso não muda no meio).
 */
export function SessionEffortControl({ topic, onChange }: { topic: ConversationTopicDto; onChange: (next: ConversationTopicDetailDto) => void }) {
  const [catalog, setCatalog] = useState<ConversationSettingsDetailDto | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const fresh = !topic.context.providerSessionId;
  const turnActive = Boolean(topic.activeTurnTaskRunId) || topic.queue.some(s => ['queued', 'working', 'deferred'].includes(s.status));
  useEffect(() => {
    if (!fresh || catalog) return;
    let alive = true;
    // Catálogo indisponível (ou cliente sem a rota) cai na lista fixa; nunca derruba a tela da Conversa.
    void (async () => {
      try { const next = await api.conversationSettings(topic.id); if (alive) setCatalog(next); }
      catch { /* lista fixa de esforços */ }
    })();
    return () => { alive = false; };
  }, [fresh, catalog, topic.id]);
  // DD.18 — resposta de catálogo em formato inesperado (servidor antigo, proxy) cai na lista fixa, não derruba a Conversa.
  const provider = (catalog?.preferences?.provider ?? topic.context?.provider ?? 'claude') as 'claude' | 'codex';
  const preferred = catalog?.preferences?.byProvider?.[provider]?.effort ?? null;
  const offered = catalog?.catalogs?.[provider]?.efforts?.map(e => e.value).filter((v): v is (typeof EFFORTS)[number] => (EFFORTS as readonly string[]).includes(v)) ?? [];
  const efforts = offered.length ? offered : [...EFFORTS];
  const current = topic.context?.sessionEffort ?? null;
  const choose = async (value: string) => {
    setBusy(true); setError('');
    try { onChange(await api.setConversationSessionEffort(topic.id, topic.revision, value === '' ? null : value)); }
    catch (cause) { setError(String(cause).includes('session_live') ? 'A sessão já começou: para trocar o esforço, reinicie o contexto e escolha antes da primeira mensagem.' : 'Não foi possível guardar o esforço desta sessão. A preferência global não mudou.'); }
    finally { setBusy(false); }
  };
  if (!fresh) {
    return <p className="topic-session-effort" data-testid="topic-session-effort" data-live="true">
      Esforço desta sessão: <strong>{effortDisplayName(current ?? topic.context.effort ?? preferred)}</strong>{current ? ' (escolhido para esta sessão)' : ' (padrão da Conversa)'} · para trocar, reinicie o contexto e escolha antes da primeira mensagem.
    </p>;
  }
  return <div className="topic-session-effort" data-testid="topic-session-effort" data-live="false">
    <label>Esforço desta sessão
      <select aria-label="esforço desta sessão" value={current ?? ''} disabled={busy || turnActive} onChange={event => void choose(event.target.value)}>
        <option value="">padrão da Conversa{preferred ? ` (${effortDisplayName(preferred)})` : ''}</option>
        {efforts.map(value => <option key={value} value={value}>{effortDisplayName(value)}</option>)}
      </select>
    </label>
    <small>Vale só para a sessão que vai começar e termina no próximo reinício. Não altera a preferência global nem os terminais fora do TermDeck.</small>
    {error ? <small role="alert">{error}</small> : null}
  </div>;
}
